/**
 * The single-file view: a preview, its details, and the actions on it.
 *
 * What may render inline is decided by the server from the bytes on disk, so
 * `previewable` here is a display hint, not a security decision - /raw refuses
 * anything outside the allowlist however this component asks for it.
 *
 * Images deliberately preview through the thumbnail route rather than /raw:
 * that gets a right-sized JPEG instead of an 8 MB original, and it is what
 * makes a HEIC from an iPhone visible in a browser at all.
 */
import { useState } from 'react';
import { Download, FolderInput, Pencil, Share2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { downloadUrl, rawUrl, thumbUrl } from '../api/client';
import { useDeleteFile, useRenameFile } from '../api/hooks';
import { formatBytes, formatDateTime, shortType } from '../lib/format';
import { Button, Field, Input, Modal, Pill, TextArea, useConfirm } from './ui';
import type { FileDto } from '../api/types';

export function FileViewer({
  file,
  onOpenChange,
  onShare,
  onAddToCollection,
}: {
  file: FileDto | null;
  onOpenChange: (open: boolean) => void;
  onShare: (file: FileDto) => void;
  onAddToCollection: (file: FileDto) => void;
}) {
  const rename = useRenameFile();
  const remove = useDeleteFile();
  const confirm = useConfirm();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  if (!file) return null;

  const startEditing = () => {
    setName(file.name);
    setDescription(file.description);
    setEditing(true);
  };

  const save = async () => {
    try {
      await rename.mutateAsync({ id: file.id, name, description });
      setEditing(false);
      toast.success('Saved');
    } catch (error) {
      toast.error('Could not save', { description: (error as Error).message });
    }
  };

  const onDelete = async () => {
    const ok = await confirm({
      title: `Delete “${file.name}”?`,
      body: 'It stops showing up for everyone it was shared with. An admin can restore it.',
      confirmLabel: 'Delete',
    });
    if (!ok) return;
    try {
      await remove.mutateAsync(file.id);
      onOpenChange(false);
      toast.success('Deleted');
    } catch (error) {
      toast.error('Could not delete', { description: (error as Error).message });
    }
  };

  const isImage = file.mimeType.startsWith('image/');
  const isVideo = file.mimeType.startsWith('video/');
  const isAudio = file.mimeType.startsWith('audio/');
  const isPdf = file.mimeType === 'application/pdf';

  return (
    <Modal
      open={Boolean(file)}
      onOpenChange={onOpenChange}
      title={file.name}
      wide
      footer={
        <>
          {file.role === 'owner' ? (
            <Button variant="ghost" onClick={onDelete} loading={remove.isPending}>
              <Trash2 className="size-4 text-danger" aria-hidden />
              Delete
            </Button>
          ) : null}
          <div className="flex-1" />
          <Button onClick={() => onAddToCollection(file)}>
            <FolderInput className="size-4" aria-hidden />
            Add to album
          </Button>
          {file.role === 'owner' ? (
            <Button onClick={() => onShare(file)}>
              <Share2 className="size-4" aria-hidden />
              Share
            </Button>
          ) : null}
          <a
            href={downloadUrl(file.id)}
            download
            className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-transparent bg-brand px-3.5 text-sm font-medium text-brand-ink hover:opacity-90"
          >
            <Download className="size-4" aria-hidden />
            Download
          </a>
        </>
      }
    >
      <div className="mb-4 flex items-center justify-center overflow-hidden rounded-xl bg-surface-2">
        {isImage && file.thumbnailable ? (
          <img
            src={thumbUrl(file.id, 1600)}
            alt={file.name}
            className="max-h-[55dvh] w-auto object-contain"
          />
        ) : isVideo && file.previewable ? (
          <video src={rawUrl(file.id)} controls playsInline className="max-h-[55dvh] w-full" />
        ) : isAudio && file.previewable ? (
          <audio src={rawUrl(file.id)} controls className="w-full p-6" />
        ) : isPdf && file.previewable ? (
          <iframe src={rawUrl(file.id)} title={file.name} className="h-[55dvh] w-full" />
        ) : (
          <p className="px-6 py-12 text-center text-sm text-ink-dim">
            No preview for {shortType(file.mimeType)} files. Download it to open it.
          </p>
        )}
      </div>

      {editing ? (
        <>
          <Field label="Name">
            <Input value={name} onChange={(event) => setName(event.target.value)} />
          </Field>
          <Field label="Description">
            <TextArea
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="What is this?"
            />
          </Field>
          <div className="flex gap-2">
            <Button variant="primary" loading={rename.isPending} onClick={save}>
              Save
            </Button>
            <Button onClick={() => setEditing(false)}>Cancel</Button>
          </div>
        </>
      ) : (
        <>
          {file.description ? <p className="mb-4 text-sm">{file.description}</p> : null}
          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-3">
            <Detail label="Type" value={shortType(file.mimeType)} />
            <Detail label="Size" value={formatBytes(file.sizeBytes)} />
            {file.width && file.height ? (
              <Detail label="Dimensions" value={`${file.width} × ${file.height}`} />
            ) : null}
            <Detail label="Uploaded" value={formatDateTime(file.createdAt)} />
            {file.takenAt ? <Detail label="Taken" value={formatDateTime(file.takenAt)} /> : null}
            <Detail label="Owner" value={file.ownerName} />
            {file.folderPath ? <Detail label="Folder" value={file.folderPath} /> : null}
          </dl>

          <div className="mt-4 flex items-center gap-2">
            {file.role !== 'viewer' ? (
              <Button onClick={startEditing}>
                <Pencil className="size-4" aria-hidden />
                Edit details
              </Button>
            ) : (
              <Pill>View only</Pill>
            )}
          </div>
        </>
      )}
    </Modal>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-ink-dim">{label}</dt>
      <dd className="truncate">{value}</dd>
    </div>
  );
}
